import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { AttendeeBody, EnviarGrupoDTO, EventAttendeeDTO, EventDetailDTO, ExtraSaleDTO, GraduadaRowDTO, HistoriaDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Chip, blobErrorText, cuando, diaMes, dinero, errorText, guardarBlob, slug } from "./comun";
import { InvitacionChip, PagoChip, invitadosTexto } from "./graduadasTab";
import QueVaAPasar from "./queVaAPasar";
import BajaPanel from "./bajaPanel";

interface Props {
	campusId: number;
	evento: EventDetailDTO;
	fila: GraduadaRowDTO;
	soloLectura: boolean;
	onClose: () => void;
	reload: () => void;
	notify: (t: string, ok?: boolean) => void;
}

type Modo = { t: "ver" } | { t: "invitados" } | { t: "invitadosQvp"; items: Omit<AttendeeBody, "eventId">[] } | { t: "extra" } | { t: "extraQvp"; para: number; key: number }
	| { t: "baja"; a: EventAttendeeDTO } | { t: "nombre"; a: EventAttendeeDTO } | { t: "quitarExtra"; x: ExtraSaleDTO };

/** Resultado del envío por canal: "WhatsApp enviado · Correo enviado" o el motivo. */
const textoEnvio = (r: EnviarGrupoDTO): { texto: string; ok: boolean } => {
	const wa = r.whatsapp === "ok" ? S.waEnviado : S.waError(r.whatsapp);
	const mail = r.email === "ok" ? S.correoEnviado : r.email === "sin correo" ? S.sinCorreo : S.correoError(r.email);
	return { texto: wa + " · " + mail, ok: r.whatsapp === "ok" && (r.email === "ok" || r.email === "sin correo") };
};

/** Una línea del expediente: lo esencial de cada paso, con fecha corta. */
const Historia: React.FC<{ h: HistoriaDTO[]; cur: any }> = ({ h, cur }) => (
	<div className="wevHistoria">
		{h.map((x, i) => {
			const oro = x.type === "cargoAnulado" || x.type === "pagoPorAprobar" || x.type === "baja" || (x.type === "whatsapp" && x.status === "fallido") || (x.type === "entrada" && x.status === "rechazada");
			const titulo = x.type === "entrada" && x.status && x.status !== "entrada" ? S.h[x.status] || x.status : S.h[x.type] || x.type;
			const cuandoTxt = x.type === "entrada" ? cuando(x.at, true) : diaMes(x.at);
			const resto = [
				x.type === "invitado" || x.type === "baja" || x.type === "entrada" ? x.name : null,
				x.type === "extra" || x.type === "extraEntregado" ? x.text : null,
				x.type === "invitacion" ? (x.text || "").split(",").filter(Boolean).map(v => S.via[v] || v).join(S.y) : null,
				x.type === "whatsapp" ? (x.status ? S.waStatus[x.status] || x.status : null) : null,
				x.type === "bajaPagada" ? x.text : null,
				x.amount !== undefined && x.amount !== null && x.type !== "extraEntregado" ? dinero(cur, x.amount) : null
			].filter(Boolean).join(" · ");
			return <span key={i} className={oro ? "oro" : undefined}><b>{titulo}</b> {cuandoTxt}{resto ? " · " + resto : ""}</span>;
		})}
	</div>
);

/**
 * Ventana de una graduada (Módulos Claros, 2026-11-01): sus invitados, extras, mesa, el expediente en una línea y los botones.
 * Todo lo que cobra pasa por «Qué Va a Pasar» (el servidor corre la misma operación y la deshace) antes de confirmar.
 */
const VentanaGraduada: React.FC<Props> = ({ campusId, evento, fila, soloLectura, onClose, reload, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [modo, setModo] = useState<Modo>({ t: "ver" });
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [historia, setHistoria] = useState<HistoriaDTO[] | null>(null);
	const [envio, setEnvio] = useState<{ texto: string; ok: boolean } | null>(null);
	// + Invitados
	const [cuantos, setCuantos] = useState<string>("1");
	const [nombres, setNombres] = useState<string>("");
	const [nino, setNino] = useState<boolean>(false);
	// Vender Extra
	const [para, setPara] = useState<number>(fila.hostAttendeeId);
	const [key, setKey] = useState<number>(evento.extras.length === 1 ? evento.extras[0].key || 0 : 0);
	// Nombre
	const [nombre, setNombre] = useState<string>("");

	const miembros = evento.attendees.filter(a => fila.memberIds.includes(a.id));
	const host = miembros.find(a => a.id === fila.hostAttendeeId) || null;
	const invitados = miembros.filter(a => a.id !== fila.hostAttendeeId);
	const extras = (evento.extraSales || []).filter(x => fila.memberIds.includes(x.attendeeId));
	const m = (v: number) => dinero(evento.currency, v);
	const tiers = (evento.guestTiers || []).map(t => t.guests);

	useEffect(() => {
		let vigente = true;
		http.eventsService.historial(campusId, fila.hostAttendeeId).then(r => { if (vigente) setHistoria(Array.isArray(r.data) ? r.data : []); }).catch(() => { if (vigente) setHistoria([]); });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, fila.hostAttendeeId, evento]);

	const volver = () => { setModo({ t: "ver" }); setError(null); };
	const hecho = (texto: string, ok = true) => { notify(texto, ok); setModo({ t: "ver" }); setBusy(false); reload(); };

	// ---------------------------------------------------------------- acciones
	const listaInvitados = (): Omit<AttendeeBody, "eventId">[] => {
		const dados = nombres.split("\n").map(x => x.trim()).filter(x => x.length > 1);
		const total = Math.min(20, Math.max(dados.length, Math.floor(Number(cuantos) || 0)));
		return Array.from({ length: total }, (_x, i) => dados[i]
			? { kind: "invitado", hostAttendeeId: fila.hostAttendeeId, name: dados[i], isChild: nino, charge: true }
			: { kind: "invitado", hostAttendeeId: fila.hostAttendeeId, namePending: true, isChild: nino, charge: true });
	};
	/** Uno tras otro, los mismos cuerpos que se mostraron en la vista previa (cada invitado toma el precio de su lugar). */
	const agregarInvitados = async (items: Omit<AttendeeBody, "eventId">[]) => {
		setBusy(true); setError(null);
		let n = 0;
		try {
			for (const it of items) { await http.eventsService.addAttendee(campusId, { eventId: evento.id, ...it } as AttendeeBody); n++; }
			setNombres(""); setCuantos("1"); setNino(false);
			hecho(S.agregadoOk(n));
		} catch (e) {
			setBusy(false);
			setError((n ? S.agregadoOk(n) + " " : "") + errorText(e));
			if (n) reload();
		}
	};
	const venderExtra = (aid: number, k: number) => {
		setBusy(true); setError(null);
		http.eventsService.addExtra(campusId, aid, k).then(() => hecho(S.extraOk)).catch(e => { setBusy(false); setError(errorText(e)); });
	};
	const enviar = () => {
		setBusy(true); setEnvio(null);
		http.eventsService.enviarGrupo(campusId, fila.hostAttendeeId)
			.then(r => { const t = textoEnvio(r.data); setEnvio(t); notify(t.texto, t.ok); reload(); })
			.catch(e => setEnvio({ texto: errorText(e), ok: false }))
			.finally(() => setBusy(false));
	};
	const copiar = async () => { try { await navigator.clipboard.writeText(fila.url); notify(S.copiado); } catch { window.open(fila.url, "_blank"); } };
	const pdf = (a: EventAttendeeDTO, diploma = false) => {
		const req = diploma ? http.eventsService.diplomaPdf(campusId, a.id) : http.eventsService.invitationPdf(campusId, a.id);
		req.then(r => guardarBlob(r.data, `${diploma ? "Diploma" : "Invitacion"}_${slug(a.name)}.pdf`)).catch(async e => notify(await blobErrorText(e), false));
	};
	const entregar = (x: ExtraSaleDTO) => {
		setBusy(true);
		http.eventsService.deliverExtra(campusId, x.id, !x.deliveredAt).then(() => reload()).catch(e => notify(errorText(e), false)).finally(() => setBusy(false));
	};
	const quitarExtra = (x: ExtraSaleDTO) => {
		setBusy(true);
		http.eventsService.removeExtra(campusId, x.id).then(() => hecho(S.quitar + ": " + x.concept)).catch(e => { setBusy(false); setError(errorText(e)); });
	};
	const guardarNombre = (a: EventAttendeeDTO) => {
		setBusy(true);
		http.eventsService.updateAttendee(campusId, { attendeeId: a.id, name: nombre.trim() }).then(() => hecho(S.ponerNombre + ": " + nombre.trim())).catch(e => { setBusy(false); setError(errorText(e)); });
	};

	// ---------------------------------------------------------------- piezas
	const pagoDe = (a: EventAttendeeDTO) => {
		if (a.freeByPackage) return <span className="wevSub">{S.gratisPaquete}</span>;
		if (a.charge && a.charge.statusId !== 4) return a.paid ? <span className="wevSub">{m(a.charge.total)}</span> : <Chip tono="oro">{S.falta(m(a.charge.pending))}</Chip>;
		return <span className="wevSub">{a.paid ? S.pagado : S.sinCobro}</span>;
	};
	const invitacionTxt = () => {
		const via = (fila.sentVia || []).map(v => S.via[v] || v).join(S.y);
		return <><InvitacionChip r={fila} />{fila.sentAt ? <div className="wevSub">{S.enviadaCuando(cuando(fila.sentAt), via)}</div> : null}
			{fila.needsResend ? <div className="wevOro" style={{ fontSize: 13 }}>{S.porReenviar}</div> : null}
			{fila.sentAt && !fila.phoneOk ? <div className="wevOro" style={{ fontSize: 13 }}>{S.invitacion.sinTelefono}</div> : null}</>;
	};
	const editable = !soloLectura && !busy;

	const cuerpoAccion = () => {
		switch (modo.t) {
			case "invitados": {
				const items = listaInvitados();
				return (
					<div className="wevQvp">
						<h6>{S.masInvitados}</h6>
						<div className="wau-fila" style={{ alignItems: "flex-start" }}>
							<label className="wau-campo" style={{ width: 90 }}><span className="wau-lab">{S.cuantos}</span>
								<input type="number" min={1} max={20} step={1} className="wau-entrada wau-num" value={cuantos} onChange={e => setCuantos(e.target.value)} autoFocus /></label>
							<label className="wau-campo" style={{ flex: "1 1 220px" }}><span className="wau-lab">{S.nombres}</span>
								<textarea className="wau-entrada" rows={2} value={nombres} onChange={e => setNombres(e.target.value)} /></label>
						</div>
						<label className="wev-check" style={{ marginTop: 6 }}><input type="checkbox" checked={nino} onChange={e => setNino(e.target.checked)} /><span>{S.esNino}</span></label>
						<div className="acciones">
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={volver}>{S.volver}</button>
							<button type="button" className="btn btn-primary btn-sm" disabled={items.length === 0} onClick={() => setModo({ t: "invitadosQvp", items })}>{S.revisar}</button>
						</div>
					</div>
				);
			}
			case "invitadosQvp":
				return <QueVaAPasar campusId={campusId} titulo={S.qvp + " · " + S.masInvitados} busy={busy} confirmar={S.confirmar}
					pedir={() => http.eventsService.previewAdd(campusId, { eventId: evento.id, action: "attendees", items: modo.items })}
					onVolver={() => setModo({ t: "invitados" })} onConfirmar={() => agregarInvitados(modo.items)} />;
			case "extra":
				return (
					<div className="wevQvp">
						<h6>{S.venderExtra}</h6>
						<div className="wau-fila">
							<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.extra}</span>
								<select className="wau-entrada" value={key} onChange={e => setKey(Number(e.target.value))}>
									<option value={0}>{S.elegir}</option>
									{evento.extras.map(x => <option key={x.key} value={x.key}>{x.concept} · {m(x.price)}</option>)}
								</select></label>
							{miembros.length > 1 ? <label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.para}</span>
								<select className="wau-entrada" value={para} onChange={e => setPara(Number(e.target.value))}>
									{miembros.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
								</select></label> : null}
						</div>
						<div className="acciones">
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={volver}>{S.volver}</button>
							<button type="button" className="btn btn-primary btn-sm" disabled={!key} onClick={() => setModo({ t: "extraQvp", para, key })}>{S.revisar}</button>
						</div>
					</div>
				);
			case "extraQvp":
				return <QueVaAPasar campusId={campusId} titulo={S.qvp + " · " + S.venderExtra} busy={busy} confirmar={S.confirmar}
					pedir={() => http.eventsService.previewAdd(campusId, { eventId: evento.id, action: "extra", attendeeId: modo.para, key: modo.key })}
					onVolver={() => setModo({ t: "extra" })} onConfirmar={() => venderExtra(modo.para, modo.key)} />;
			case "baja":
				return <BajaPanel campusId={campusId} evento={evento} asistente={modo.a} onVolver={volver}
					onDone={(t) => { notify(t); if (modo.a.id === fila.hostAttendeeId) { reload(); onClose(); } else { hecho(t); } }} />;
			case "nombre":
				return (
					<div className="wevQvp">
						<h6>{S.nombreTitulo(modo.a.name)}</h6>
						<input className="wau-entrada" style={{ width: "100%" }} maxLength={150} value={nombre} onChange={e => setNombre(e.target.value)} autoFocus
							onKeyDown={e => { if (e.key === "Enter" && nombre.trim().length > 1) guardarNombre(modo.a); }} />
						<div className="acciones">
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={volver} disabled={busy}>{S.volver}</button>
							<button type="button" className="btn btn-primary btn-sm" disabled={busy || nombre.trim().length < 2} onClick={() => guardarNombre(modo.a)}>{S.guardar}</button>
						</div>
					</div>
				);
			case "quitarExtra":
				return (
					<div className="wevQvp">
						<h6>{S.qvp} · {S.quitar} {modo.x.concept}</h6>
						<ul><li>{S.qvpAnula(modo.x.concept, m(modo.x.charge ? modo.x.charge.total : modo.x.price))}</li></ul>
						<div className="acciones">
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={volver} disabled={busy}>{S.volver}</button>
							<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => quitarExtra(modo.x)}>{S.confirmar}</button>
						</div>
					</div>
				);
			default: return null;
		}
	};

	return (
		<Modal className="waModulo wevVentana" show onHide={busy ? undefined : onClose} size="lg" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: movil ? 16 : 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
					<span>{fila.name}</span><PagoChip r={fila} cur={evento.currency} />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>
				<div className="wevDatos">
					<div className="wevDato"><small>{S.curso}</small>{cursoCorto(fila.course) || fila.course || "-"}<div className="wevSub">{[fila.kind === "participante" ? S.participante : null, fila.code].filter(Boolean).join(" · ")}</div></div>
					<div className="wevDato"><small>{S.invitados}</small>{invitadosTexto(fila, tiers)}</div>
					<div className="wevDato"><small>{S.pago}</small>{S.pagadoDe(m(fila.paidAmount), m(fila.charged))}</div>
					<div className="wevDato"><small>{S.colInvitacion}</small>{invitacionTxt()}</div>
					{fila.tableNo ? <div className="wevDato"><small>{S.mesa}</small>{fila.tableNo}</div> : null}
					{host?.checkedInAt ? <div className="wevDato"><small>{S.colEntrada}</small>{cuando(host.checkedInAt, true)}</div> : null}
				</div>

				{invitados.length ? (
					<div className="wevSeccion">
						<h6>{S.invitados} ({invitados.length})</h6>
						{invitados.map(a => (
							<div key={a.id} className="wevRenglon">
								<div className="izq">
									<span className={a.namePending ? "wevOro" : undefined}>{a.name}</span>
									{a.isChild ? <Chip>{S.esNino}</Chip> : null}
									{a.tableNo ? <span className="wevSub">{S.mesa} {a.tableNo}</span> : null}
									{a.checkedInAt ? <span className="wevSub">{S.colEntrada} {cuando(a.checkedInAt, true)}</span> : null}
								</div>
								<div className="der">
									{pagoDe(a)}
									{a.namePending && editable ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setNombre(""); setModo({ t: "nombre", a }); }}>{S.ponerNombre}</button> : null}
									<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => pdf(a)}>{S.pdf}</button>
									{editable ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModo({ t: "baja", a })}>{S.baja}</button> : null}
								</div>
							</div>
						))}
					</div>
				) : null}

				{extras.length ? (
					<div className="wevSeccion">
						<h6>{S.extrasTitulo}</h6>
						{extras.map(x => (
							<div key={x.id} className="wevRenglon">
								<div className="izq"><span>{x.concept}</span>{x.attendeeId !== fila.hostAttendeeId ? <span className="wevSub">{x.name}</span> : null}
									{x.paid ? <span className="wevSub">{m(x.charge ? x.charge.total : x.price)}</span> : <Chip tono="oro">{S.falta(m(x.charge ? x.charge.pending : x.price))}</Chip>}</div>
								<div className="der">
									{x.deliveredAt ? <Chip>{S.entregado(diaMes(x.deliveredAt))}</Chip> : <Chip tono="oro">{S.sinEntregar}</Chip>}
									{soloLectura ? null : <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => entregar(x)}>{x.deliveredAt ? S.quitarEntrega : S.entregar}</button>}
									{soloLectura || x.paid || (x.charge && x.charge.paid > 0) ? null : <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} aria-label={S.quitar} onClick={() => setModo({ t: "quitarExtra", x })}>×</button>}
								</div>
							</div>
						))}
					</div>
				) : null}

				<div className="wevSeccion">
					<h6>{S.historia}</h6>
					{historia === null ? <div className="wevSub">{S.cargando}</div> : <Historia h={historia} cur={evento.currency} />}
				</div>

				{envio ? <div className={envio.ok ? "wevSub" : "wevOro"} style={{ marginTop: 10 }}>{envio.texto}</div> : null}
				{error ? <div className="wevOro" style={{ marginTop: 10, fontWeight: 600 }}>{error}</div> : null}
				{cuerpoAccion()}
			</Modal.Body>
			{modo.t === "ver" ? (
				<Modal.Footer className="wevPie">
					{soloLectura ? null : <>
						<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => setModo({ t: "invitados" })}>{S.masInvitados}</button>
						{evento.extras.length ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setModo({ t: "extra" })}>{S.venderExtra}</button> : null}
						<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy || !fila.fullyPaid} onClick={enviar}>{busy ? S.enviando : fila.sentAt ? S.reenviarInvitacion : S.enviarInvitacion}</button>
					</>}
					<button type="button" className="btn btn-outline-secondary btn-sm" onClick={copiar}>{S.copiarEnlace}</button>
					{host ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => pdf(host)}>{S.pdf}</button> : null}
					{host?.diploma?.ready ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => pdf(host, true)}>{S.diplomaPdf}</button> : null}
					{soloLectura || !host ? null : <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setModo({ t: "baja", a: host })}>{S.baja}</button>}
				</Modal.Footer>
			) : null}
		</Modal>
	);
};

export default VentanaGraduada;
